import { useEffect, useRef, type ReactNode } from 'react';
import { westernDigits } from './westernDigits';

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'DIV', 'SPAN']);

/** Keep only bold / italic / underline / breaks / simple blocks. */
export function sanitizeTermsHtml(html: string): string {
  if (typeof document === 'undefined') return stripTagsFallback(html);
  const template = document.createElement('template');
  template.innerHTML = html;
  const walk = (node: Node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (!ALLOWED_TAGS.has(el.tagName)) {
          while (el.firstChild) node.insertBefore(el.firstChild, el);
          node.removeChild(el);
          continue;
        }
        // Drop all attributes (no style/onclick).
        for (const attr of Array.from(el.attributes)) el.removeAttribute(attr.name);
        walk(el);
      } else if (child.nodeType !== Node.TEXT_NODE) {
        node.removeChild(child);
      }
    }
  };
  walk(template.content);
  return template.innerHTML;
}

function stripTagsFallback(html: string): string {
  return html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/<\/?(?!\/?(?:b|strong|i|em|u|br|p|div|span)\b)[^>]+>/gi, '');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Convert plain / legacy markdown terms into editor HTML. */
export function termsToEditorHtml(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/<[a-z][\s\S]*>/i.test(trimmed)) return sanitizeTermsHtml(trimmed);

  let html = escapeHtml(raw);
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<u>$1</u>');
  html = html.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>');
  html = html.replace(/\n/g, '<br>');
  return html;
}

/** Plain text for teasers (strips editor HTML). */
export function plainTextFromMarkup(raw: string): string {
  const source = raw.trim();
  if (!source) return '';
  if (typeof document !== 'undefined' && /<[a-z][\s\S]*>/i.test(source)) {
    const div = document.createElement('div');
    div.innerHTML = sanitizeTermsHtml(source);
    return div.innerText.replace(/\s+/g, ' ').trim();
  }
  return source.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function looksLikeHtml(text: string): boolean {
  return /<\/?(?:b|strong|i|em|u|br|p|div|span)\b/i.test(text);
}

/**
 * Renders terms content: prefers HTML from the rich editor, falls back to legacy ** * __ markers.
 */
export function FormattedText({
  text,
  className = '',
}: {
  text: string;
  className?: string;
}) {
  if (!text.trim()) return null;

  if (looksLikeHtml(text)) {
    return (
      <div
        className={`leading-relaxed [&_strong]:font-semibold [&_b]:font-semibold [&_em]:italic [&_i]:italic [&_u]:underline ${className}`.trim()}
        dangerouslySetInnerHTML={{ __html: sanitizeTermsHtml(westernDigits(text)) }}
      />
    );
  }

  return <LegacyMarkdownText text={westernDigits(text)} className={className} />;
}

function LegacyMarkdownText({ text, className }: { text: string; className: string }) {
  const MARKUP_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__)/g;
  const renderInline = (line: string, keyPrefix: string): ReactNode[] => {
    const parts = line.split(MARKUP_RE);
    return parts.map((part, i) => {
      const key = `${keyPrefix}-${i}`;
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={key} className="font-semibold text-ink">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('__') && part.endsWith('__') && part.length > 4) {
        return <u key={key}>{part.slice(2, -2)}</u>;
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2 && !part.startsWith('**')) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }
      return <span key={key}>{part}</span>;
    });
  };

  const blocks = text.replace(/\r\n/g, '\n').split(/\n{2,}/);
  return (
    <div className={`space-y-3 ${className}`.trim()}>
      {blocks.map((block, bi) => {
        const lines = block.split('\n');
        return (
          <p key={bi} className="whitespace-pre-wrap leading-relaxed">
            {lines.map((line, li) => (
              <span key={`${bi}-${li}`}>
                {li > 0 ? <br /> : null}
                {renderInline(line, `${bi}-${li}`)}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

type RichTextEditorProps = {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
};

function ToolbarButton({
  label,
  title,
  active,
  className = '',
  onClick,
}: {
  label: string;
  title: string;
  active?: boolean;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={`min-w-[32px] h-8 px-2 rounded text-sm text-ink border transition-colors ${
        active ? 'bg-teal-soft border-teal text-teal' : 'bg-surface border-transparent hover:border-line hover:bg-bg'
      } ${className}`}
      onMouseDown={e => {
        e.preventDefault();
        onClick();
      }}
    >
      {label}
    </button>
  );
}

/** Word-style editor: select text, then Bold / Italic / Underline from the bar. */
export function RichTextEditor({
  label,
  value,
  onChange,
  rows = 8,
  className = '',
  placeholder = 'Write terms…',
  'aria-label': ariaLabel,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef(value);
  const minHeight = Math.max(120, rows * 22);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value === lastEmitted.current) return;
    if (document.activeElement === el) return;
    el.innerHTML = termsToEditorHtml(value);
    lastEmitted.current = value;
  }, [value]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    el.innerHTML = termsToEditorHtml(value);
    lastEmitted.current = value;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial content only

  const emit = () => {
    const el = editorRef.current;
    if (!el) return;
    const html = sanitizeTermsHtml(el.innerHTML);
    const plain = el.innerText.replace(/\u00a0/g, ' ').trim();
    const next = plain ? html : '';
    lastEmitted.current = next;
    onChange(next);
  };

  const run = (command: 'bold' | 'italic' | 'underline') => {
    editorRef.current?.focus();
    document.execCommand(command, false);
    emit();
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`.trim()}>
      {label ? <label className="text-xs font-medium text-ink-soft">{label}</label> : null}
      <div className="border border-line rounded-radius-sm overflow-hidden bg-surface focus-within:ring-2 focus-within:ring-sand/40 focus-within:border-sand">
        <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-line bg-bg">
          <ToolbarButton label="B" title="Bold" className="font-bold" onClick={() => run('bold')} />
          <ToolbarButton label="I" title="Italic" className="italic" onClick={() => run('italic')} />
          <ToolbarButton label="U" title="Underline" className="underline" onClick={() => run('underline')} />
          <span className="ml-2 text-[11px] text-ink-faint hidden sm:inline">Select text, then format</span>
        </div>
        <div
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel || label || 'Terms'}
          contentEditable
          suppressContentEditableWarning
          className="px-3 py-2 text-sm text-ink leading-relaxed outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-ink-faint"
          style={{ minHeight }}
          data-placeholder={placeholder}
          onInput={emit}
          onBlur={emit}
        />
      </div>
    </div>
  );
}
